import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search, User, Menu, X, ShoppingCart } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useCart } from '../context/CartContext';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const isLoggedIn = localStorage.getItem('userToken');
  const { getCartCount } = useCart();

  // Menu items - Blog y Promociones eliminados permanentemente
  const menuItems = [
    { name: 'Estudios', path: '/estudios' },
    { name: 'Sucursales', path: '/sucursales' },
    { name: 'Resultados', path: '/resultados' },
    { name: 'Facturación', path: '/facturacion' }
  ];

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/estudios?search=${searchQuery}`);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('userToken');
    localStorage.removeItem('userName');
    navigate('/');
  };

  return (
    <>
      {/* Main navbar */}
      <nav className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <div className="bg-black px-4 py-2 rounded-lg">
                <img 
                  src="https://customer-assets.emergentagent.com/job_morabio-web/artifacts/o1ie7x5d_LogoMoraBioNegro.jpg" 
                  alt="MoraBioVet" 
                  className="h-24 w-auto"
                />
              </div>
            </Link>

            {/* Search bar - Desktop */}
            <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-md mx-8">
              <div className="relative w-full">
                <Input
                  type="text"
                  placeholder="¿Qué buscas? ¿Te podemos ayudar?"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pr-10 border-gray-300"
                />
                <Button 
                  type="submit"
                  className="absolute right-0 top-0 h-full px-4 bg-[#1e40af] hover:bg-[#1e3a8a]"
                >
                  <Search className="h-4 w-4" />
                </Button>
              </div>
            </form>

            {/* Right side buttons */}
            <div className="flex items-center gap-4">
              {/* Carrito */}
              <Link to="/carrito" className="relative">
                <Button variant="ghost" className="relative">
                  <ShoppingCart className="h-5 w-5" />
                  {getCartCount() > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
                      {getCartCount()}
                    </span>
                  )}
                </Button>
              </Link>

              {isLoggedIn ? (
                <div className="hidden md:flex items-center gap-2">
                  <Link to="/dashboard">
                    <Button variant="ghost" className="flex items-center gap-2">
                      <User className="h-5 w-5" />
                      <span>{localStorage.getItem('userName') || 'Mi cuenta'}</span>
                    </Button>
                  </Link>
                  <Button 
                    variant="outline" 
                    onClick={handleLogout}
                    className="text-sm"
                  >
                    Cerrar sesión
                  </Button>
                </div>
              ) : (
                <Link to="/login" className="hidden md:block">
                  <Button variant="ghost" className="flex items-center gap-2">
                    <User className="h-5 w-5" />
                    <span>Inicia sesión</span>
                  </Button>
                </Link>
              )}

              {/* Mobile menu button */}
              <Button
                variant="ghost"
                className="md:hidden"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Main menu */}
        <div className="border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-4">
            <div className="hidden md:flex items-center justify-center gap-8 py-3">
              {menuItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`text-sm font-medium transition-colors hover:text-[#1e40af] ${
                    location.pathname === item.path
                      ? 'text-[#1e40af] border-b-2 border-[#1e40af] pb-1'
                      : 'text-gray-700'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-gray-200 bg-white">
            <div className="px-4 py-3 space-y-3">
              {/* Mobile search */}
              <form onSubmit={handleSearch} className="mb-4">
                <div className="relative">
                  <Input
                    type="text"
                    placeholder="Buscar..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full"
                  />
                </div>
              </form>

              {menuItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className="block py-2 text-gray-700 hover:text-[#1e40af] transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}

              <div className="border-t pt-3">
                {isLoggedIn ? (
                  <>
                    <Link
                      to="/dashboard"
                      className="block py-2 text-gray-700"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Mi cuenta
                    </Link>
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsMenuOpen(false);
                      }}
                      className="block py-2 text-gray-700 w-full text-left"
                    >
                      Cerrar sesión
                    </button>
                  </>
                ) : (
                  <Link
                    to="/login"
                    className="block py-2 text-gray-700"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Iniciar sesión
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default Navbar;